import {
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from '@angular/core';

import { DeviceDetectorService } from 'ngx-device-detector';

import { ELegType } from 'src/app/core/enums/leg-type.enum';
import {
  IStopover,
  IParsedTripLeg,
  ITripDestination,
} from 'src/app/core/types/interfaces/trip-elements.interface';

import { MapService } from 'src/app/core/services/map.service';

import Radar from 'radar-sdk-js';
import RadarMap from 'radar-sdk-js/dist/ui/RadarMap';
import RadarMarker from 'radar-sdk-js/dist/ui/RadarMarker';
import RadarLineFeature from 'radar-sdk-js/dist/ui/RadarLineFeature';

@Component({
  standalone: true,
  selector: 'app-stopovers-map',
  templateUrl: './stopovers-map.component.html',
  styleUrls: ['../maps.style.scss'],
})
export class StopoversMapComponent implements OnDestroy {
  @Input() destinations: ITripDestination[] = [];
  @Input() leg: IParsedTripLeg;

  private pointA: ITripDestination;
  private pointB: ITripDestination;

  private map: RadarMap;
  private mapMarkers: RadarMarker[] = [];
  private mapLines: RadarLineFeature[] = [];

  @ViewChild('map', { static: true })
  private mapElement: ElementRef<HTMLElement>;

  readonly #ngZone: NgZone = inject(NgZone);
  readonly #mapService: MapService = inject(MapService);
  readonly #deviceDetectorService: DeviceDetectorService = inject(
    DeviceDetectorService,
  );

  @Input() set cityInfoIndex(index: number) {
    if (this.destinations && this.destinations.length) {
      this.pointA = this.destinations[index];
      this.pointB = this.destinations[index + 1];
      const coords = this.pointA.coordinates.split(',').reverse();
      this.initMap(coords);

      if (this.mapMarkers.length) {
        this.mapMarkers.forEach((marker) => marker.remove());
        this.map.clearMarkers();
      }

      this.pointA.stopovers.forEach((d: IStopover, idx: number) => {
        if (idx >= 62) return;
        const el: HTMLElement =
          this.#mapService.createStopoverMarkerElement(idx);
        const marker: any | RadarMarker = Radar.ui.marker({
          element: el,
          anchor: 'center',
        });
        marker.setLngLat(d.coordinates.split(',').reverse()).addTo(this.map);
        this.mapMarkers.push(marker);
      });

      [this.pointA, this.pointB].forEach((p: ITripDestination, idx: number) => {
        const pointIndex: number = index + (idx + 1);
        const el: HTMLElement = this.#mapService.createSimpleMarkerElement(
          pointIndex,
          false,
        );
        const marker: any | RadarMarker = Radar.ui.marker({
          element: el,
          anchor: 'center',
        });
        marker.setLngLat(p.coordinates.split(',').reverse()).addTo(this.map);
        this.mapMarkers.push(marker);
      });
      this.map.fitToMarkers({ padding: 50 });
    }
  }

  /**
   * Sets the list of stopovers (`stopovers`) for constructing a route on the map.
   * Clears existing map lines and adds new ones based on the travel type.
   *
   * - If `stopovers` is empty, it checks for the presence of a `leg` (route segment).
   *   If a leg exists, it draws a route line on the map according to its type (e.g., car route).
   * - If `stopovers` contains elements, it constructs a complex route between the starting point (`pointA`),
   *   stopovers, and the endpoint (`pointB`), using the retrieved route segments.
   *
   * @param stopovers - An array of `IStopover` objects that contain coordinates of each stopover along the route.
   */
  @Input() set selectedStopovers(stopovers: IStopover[]) {
    const clearMapLines = () => {
      if (this.mapLines.length) {
        this.mapLines.forEach((line) => line.remove());
        this.map.clearFeatures();
      }
    };

    if (!stopovers.length) {
      if (this.leg && this.leg.geometryString) {
        let mapLine: RadarLineFeature;
        clearMapLines();

        if (this.leg.type === ELegType.CAR) {
          mapLine = this.map.addPolyline(
            this.leg.geometryString,
            this.#mapService.polylinePathOptions,
          );
        } else {
          const parsedGeometryString = JSON.parse(this.leg.geometryString);
          mapLine = this.map.addLine(
            parsedGeometryString.line,
            parsedGeometryString.options,
          );
        }
        this.mapLines.push(mapLine);
      }
    } else {
      const startCoords = this.pointA.coordinates;
      const endCoords = this.pointB.coordinates;
      const routes = stopovers.map((s) => s.coordinates);
      const routeString = `${startCoords}|${routes.join('|')}|${endCoords}`;

      this.#mapService
        .getDirectionsRoutesAndLegs(routeString)
        .subscribe((legs) => {
          clearMapLines();
          legs.forEach((leg) => {
            const polyline: RadarLineFeature = this.map.addPolyline(
              leg.geometry.polyline,
              this.#mapService.polylinePathOptions,
            );
            polyline.id = `polyline-feature-${this.#mapService.generateUniqueId()}`;
            this.mapLines.push(polyline);
          });
          this.map.fitToMarkers({ padding: 50 });
        });
    }
  }

  ngOnDestroy(): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;
    if (userAgent && this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private initMap(coordinates: any): void {
    const userAgent: string = this.#deviceDetectorService.userAgent;

    if (userAgent && !this.map) {
      this.#ngZone.runOutsideAngular(() => {
        this.map = Radar.ui.map({
          container: this.mapElement.nativeElement,
          style: 'radar-default-v1',
          showZoomControls: false,
          center: coordinates,
          zoom: 5,
          trackResize: true,
        });
      });
    }
  }
}
